import { useTOptions } from "@/i18n"
import { cloneElement } from "react"

type ComponentWithOptions<TProps extends Record<string, any>> =
  React.ComponentType<TProps & { options: SelectOption<string>[] }>

type UseOptions = () => SelectOption<string>[]

const OptionsProvider = ({
  useOptions,
  children,
}: {
  useOptions: UseOptions
  children: React.ReactElement<{ options: SelectOption<string>[] }>
}) => {
  const options = useOptions()
  return cloneElement(children, { options })
}

function withOptions<TProps extends Record<string, any>>(
  WrappedComponent: ComponentWithOptions<TProps>,
  useOptions: UseOptions,
): React.ComponentType<Omit<TProps, "options">> {
  const WithOptions = (props: Omit<TProps, "options">) => {
    return (
      <OptionsProvider useOptions={useOptions}>
        <WrappedComponent
          {...(props as TProps & { options: SelectOption<string>[] })}
        />
      </OptionsProvider>
    )
  }

  WithOptions.displayName = `withOptions(${
    WrappedComponent.displayName || WrappedComponent.name || "Component"
  })`

  return WithOptions
}

function createWithOptionsHoc(useOptions: () => SelectOption<string>[]) {
  return <TProps extends Record<string, any>>(
    WrappedComponent: React.ComponentType<
      TProps & { options: SelectOption<string>[] }
    >,
  ): React.ComponentType<Omit<TProps, "options">> =>
    withOptions(WrappedComponent, useOptions)
}

export const withCountryOptions = createWithOptionsHoc(() =>
  useTOptions("by_sacc_code", "countries"),
)

export const withGenderOptions = createWithOptionsHoc(() =>
  useTOptions("user.gender", "common"),
)

export const withAdhdOptions = createWithOptionsHoc(() =>
  useTOptions("adhd_options", "student_attributes_options"),
)

export const withAutismOptions = createWithOptionsHoc(() =>
  useTOptions("autism_options", "student_attributes_options"),
)

export const withEyesightOptions = createWithOptionsHoc(() =>
  useTOptions("visual_impairment_options", "student_attributes_options"),
)

export const withDepressionOptions = createWithOptionsHoc(() =>
  useTOptions("depression_options", "student_attributes_options"),
)

export const withExtentOptions = createWithOptionsHoc(() =>
  useTOptions("extent_options", "student_attributes_options"),
)

export const withGradeOptions = createWithOptionsHoc(() =>
  useTOptions("grade_options", "student_attributes_options"),
)

export const withNaplanOptions = createWithOptionsHoc(() =>
  useTOptions("naplan_options", "student_attributes_options"),
)

export const withParentEducationOptions = createWithOptionsHoc(() =>
  useTOptions("parent_education_options", "student_attributes_options"),
)

export const withParentOccupationOptions = createWithOptionsHoc(() =>
  useTOptions("parent_occupation_options", "student_attributes_options"),
)
